import {
  applyGamifyDevOverride,
  gamifyDevOverrideAtom,
} from '@/features/billing-dev/gamify-overrides';
import {
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  IS_GAMIFY_TRIAL_LENGTH_CLINICIAN_ENABLED,
  IS_GAMIFY_TRIAL_LENGTH_ENABLED,
  IS_GAMIFY_TRIAL_LENGTH_EVIDENCE_ENABLED,
} from '@/features/flags';
import { useIsGamifyTrialArmAllowed } from '@/features/post-note-quality/hooks/use-is-gamify-trial-arm-allowed';
import useReverseTrial from '@/hooks/useReverseTrial';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import type { GamifyTrialExtensionResponse } from '@heidi/commerce/contracts';
import { useQueryGamifyTrialExtension } from '@heidi/commerce/data';
import { useCombinedQueriesTeamGeneralInfo, useQueryUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

/**
 * Whether the user may see an entry point to the gamify trial-extension modal.
 * Shared by the gift nav item and the usage popover so the two cannot drift.
 * Includes backend eligibility (`eligible || trial_extended`) so entry points
 * stay hidden while loading and for ineligible accounts — the modal renders
 * nothing in those cases.
 * Dismissing the gift nav item is a nav-item concern and is not checked here.
 */
export function useIsGamifyTrialExtensionEntryEnabled(): boolean {
  const { isGateOpen, data } = useGamifyTrialExtensionEntryGate();
  return isGateOpen && !!data && (data.eligible || data.trial_extended);
}

/**
 * The gift nav item's gate: the client-side checks only, without waiting for
 * backend eligibility. Right after a trial is accepted the backend reports
 * `eligible: false` until the Stripe webhook writes the tier, so waiting on
 * it would hide the gift for those first seconds. The modal still guards on
 * `eligible` and renders once the refreshed response arrives.
 */
export function useIsGamifyTrialExtensionNavEnabled(): boolean {
  return useGamifyTrialExtensionEntryGate().isGateOpen;
}

function useGamifyTrialExtensionEntryGate() {
  const {
    isPracticeTrialUser,
    isClinicianTrialUser,
    isFakeClinicianTrial,
    isEvidenceTrialUser,
  } = useReverseTrial();
  const { isNonAdminTeamMember, isLoadingTeamDetails } =
    useCombinedQueriesTeamGeneralInfo();
  const { data: user } = useQueryUser();
  // Experiment arms: suppress (Arm B) / delay until the sidebar is done (Arm C).
  const isGamifyArmAllowed = useIsGamifyTrialArmAllowed();

  const isTeamGateOpen =
    isLoadingTeamDetails ||
    !isNonAdminTeamMember ||
    !IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED();

  const isTrialTierEnabled =
    (isPracticeTrialUser && IS_GAMIFY_TRIAL_LENGTH_ENABLED()) ||
    (isClinicianTrialUser &&
      !isFakeClinicianTrial &&
      IS_GAMIFY_TRIAL_LENGTH_CLINICIAN_ENABLED()) ||
    (isEvidenceTrialUser && IS_GAMIFY_TRIAL_LENGTH_EVIDENCE_ENABLED());

  // Fold every gate before the query call — Rules of Hooks forbids a hook
  // after an early return.
  const isGateOpen =
    !isAppleCompliantBuild() &&
    isGamifyArmAllowed &&
    isTeamGateOpen &&
    Boolean(isTrialTierEnabled);

  const devOverride = useAtomValue(gamifyDevOverrideAtom);
  const select = useCallback(
    (response: GamifyTrialExtensionResponse) =>
      applyGamifyDevOverride(response, devOverride),
    [devOverride]
  );
  const { data } = useQueryGamifyTrialExtension({
    hasUser: !!user,
    enabled: isGateOpen,
    select,
  });

  return { isGateOpen, data };
}
